import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase';
import {
  Gift,
  Video,
  Upload,
  CheckCircle,
  Clock,
  Star,
  FileText,
  AlertCircle,
  ExternalLink
} from 'lucide-react';
import ErrorBoundary from '@/shared/components/ErrorBoundary';

const TestimonialProgram: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    examType: '',
    story: '',
    videoLink: '',
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      // Persist to the testimonials collection via a validated, rate-limited
      // backend callable (lands as pending_review for admin approval).
      // submitTestimonial (aswb-backend#173) accepts a written story, a video
      // link, or both — it only requires at least one, which isFormValid
      // below mirrors.
      const submit = httpsCallable<
        { name: string; email: string; examType: string; story: string; videoLink: string; consent: boolean },
        { success: boolean }
      >(functions, 'submitTestimonial');

      await submit({
        name: formData.name,
        email: formData.email,
        examType: formData.examType,
        story: formData.story.trim(),
        videoLink: formData.videoLink.trim(),
        consent: formData.consent,
      });

      setSubmitStatus('success');
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Something went wrong submitting your testimonial. Please try again.';
      setErrorMessage(message);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid =
    formData.email &&
    formData.name &&
    formData.examType &&
    formData.consent &&
    (formData.story.trim() || formData.videoLink.trim());

  return (
    <ErrorBoundary>
      <Helmet>
        <title>Get 1 Year Free - Testimonial Program | Social Work Test Prep Academy</title>
        <meta
          name="description"
          content="Get 1 year of free access ($192 value) to Social Work Test Prep Academy in exchange for a testimonial, a written story or a short video. Share your ASWB exam prep experience!"
        />
        <meta property="og:title" content="Get 1 Year Free - Testimonial Program | SWTPA" />
        <meta property="og:description" content="Share a written story or a short video testimonial and receive 1 year of free access to our LCSW, LMSW, and LBSW exam prep platform." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.socialworktestprepacademy.com/testimonial-program" />
        {/* Clean canonical — NOT window.location.href, which folds UTM/ref into
            the canonical and self-competes every campaign link (SEO audit). */}
        <link rel="canonical" href="https://www.socialworktestprepacademy.com/testimonial-program" />
      </Helmet>

      <div className="min-h-screen bg-neutral-50">
        {/* Subtle background pattern */}
        <div
          className="absolute inset-0 opacity-[0.02] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232C5F5D' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        <div className="relative container-wide py-12 sm:py-16 lg:py-20">
          {/* Header */}
          <div className="text-center mb-12 lg:mb-16">
            <Link to="/" className="inline-flex items-center gap-3 mb-8 group">
              <div className="w-12 h-12 rounded-xl bg-primary-500 flex items-center justify-center shadow-ledge group-hover:shadow-ledge-lg transition-shadow">
                <span className="text-white font-bold text-xl" style={{ fontFamily: 'var(--font-display)' }}>S</span>
              </div>
              <span className="font-semibold text-neutral-900 text-lg" style={{ fontFamily: 'var(--font-display)' }}>
                Social Work Test Prep Academy
              </span>
            </Link>

            {/* Value Badge */}
            <div className="inline-flex items-center gap-2 bg-success-light text-success px-4 py-2 rounded-full mb-6">
              <Gift className="w-5 h-5" />
              <span className="font-semibold">$192 Value - Completely Free</span>
            </div>

            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-neutral-900 mb-4 leading-tight"
              style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.02em' }}
            >
              Get 1 Year Free
              <span className="block text-primary-500">For Your Testimonial</span>
            </h1>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Share your SWTPA experience in a written story or a short video, and receive a full year of unlimited access to our exam prep platform.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 max-w-6xl mx-auto">
            {/* LEFT COLUMN - Information */}
            <div className="space-y-8">
              {/* What We're Looking For */}
              <div className="bg-neutral-100 rounded-2xl shadow-ledge border border-neutral-100 p-6 lg:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                    <Video className="w-5 h-5 text-primary-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-neutral-900" style={{ fontFamily: 'var(--font-display)' }}>
                    What to Share
                  </h2>
                </div>

                <ul className="space-y-4">
                  {[
                    { icon: FileText, text: 'A written story works great, a few honest sentences is plenty, or record 30-60 seconds of video if you prefer' },
                    { icon: Clock, text: 'Mention which exam you\'re studying for (LCSW, LMSW, or LBSW)' },
                    { icon: Star, text: 'Share what features helped you most' },
                    { icon: CheckCircle, text: 'Be authentic - doesn\'t need to be professionally produced' }
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-success-light flex items-center justify-center flex-shrink-0 mt-0.5">
                        <item.icon className="w-3.5 h-3.5 text-success" />
                      </div>
                      <span className="text-neutral-700">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* How It Works */}
              <div className="bg-neutral-100 rounded-2xl shadow-ledge border border-neutral-100 p-6 lg:p-8">
                <h2 className="text-xl font-semibold text-neutral-900 mb-6" style={{ fontFamily: 'var(--font-display)' }}>
                  How It Works
                </h2>

                <div className="space-y-6">
                  {[
                    { step: '1', title: 'Write or Record', desc: 'Write a few sentences about your experience, or record a video on your phone, laptop, or any device' },
                    { step: '2', title: 'Submit It', desc: 'Type your story directly into the form, or upload your video to YouTube (unlisted), Loom, Google Drive, or Dropbox and share the link' },
                    { step: '3', title: 'We Review', desc: 'Our team reviews submissions within 48 hours' },
                    { step: '4', title: 'Get Your Free Year', desc: 'Once approved, we\'ll send you a promo code for 1 year of free access' }
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center font-semibold flex-shrink-0">
                        {item.step}
                      </div>
                      <div>
                        <h3 className="font-medium text-neutral-900">{item.title}</h3>
                        <p className="text-sm text-neutral-600">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recording Tips */}
              <div className="bg-primary-50 rounded-2xl p-6 border border-primary-100">
                <h3 className="font-semibold text-primary-700 mb-3">
                  Quick Recording Tips
                </h3>
                <ul className="space-y-2 text-sm text-primary-600">
                  <li>- Find a quiet spot with good lighting</li>
                  <li>- Speak naturally - no script needed!</li>
                  <li>- Horizontal orientation preferred</li>
                  <li>- Loom.com offers free, easy screen recording</li>
                </ul>
              </div>
            </div>

            {/* RIGHT COLUMN - Form */}
            <div>
              <div className="bg-neutral-100 rounded-2xl shadow-ledge-lg border border-neutral-100 overflow-hidden sticky top-8">
                {/* Form Header */}
                <div className="px-6 pt-6 pb-4 border-b border-neutral-100 bg-primary-600">
                  <h2 className="text-xl font-semibold text-white" style={{ fontFamily: 'var(--font-display)' }}>
                    Submit Your Testimonial
                  </h2>
                  <p className="text-primary-100 text-sm mt-1">
                    Write your story below, or share a video link
                  </p>
                </div>

                {/* Form Body */}
                <div className="p-6">
                  {submitStatus === 'success' ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 rounded-full bg-success-light flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-8 h-8 text-success" />
                      </div>
                      <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                        Submission Received!
                      </h3>
                      <p className="text-neutral-600 mb-4">
                        Thank you! We've got your testimonial and our team will review it within 48 hours. Once it's approved, we'll email you a promo code for your free year.
                      </p>
                      <button
                        onClick={() => {
                          setSubmitStatus('idle');
                          setFormData({ email: '', name: '', examType: '', story: '', videoLink: '', consent: false });
                        }}
                        className="text-primary-600 font-medium hover:underline"
                      >
                        Submit another testimonial
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-5">
                      {submitStatus === 'error' && errorMessage && (
                        <div className="flex items-start gap-3 p-3 rounded-xl bg-error-50 border border-error-200">
                          <AlertCircle className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-error-700">{errorMessage}</p>
                        </div>
                      )}
                      {/* Name */}
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-neutral-700 mb-2">
                          Your Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-neutral-200 bg-neutral-100 text-neutral-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                          placeholder="Jane Smith"
                        />
                      </div>

                      {/* Email */}
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-neutral-200 bg-neutral-100 text-neutral-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                          placeholder="jane@example.com"
                        />
                      </div>

                      {/* Exam Type */}
                      <div>
                        <label htmlFor="examType" className="block text-sm font-medium text-neutral-700 mb-2">
                          Which exam are you studying for? *
                        </label>
                        <select
                          id="examType"
                          name="examType"
                          value={formData.examType}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-neutral-200 bg-neutral-100 text-neutral-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                        >
                          <option value="">Select your exam</option>
                          <option value="LCSW">LCSW (Licensed Clinical Social Worker)</option>
                          <option value="LMSW">LMSW (Licensed Master Social Worker)</option>
                          <option value="LBSW">LBSW (Licensed Bachelor Social Worker)</option>
                        </select>
                      </div>

                      {/* Written Story */}
                      <div>
                        <label htmlFor="story" className="block text-sm font-medium text-neutral-700 mb-2">
                          Your Story
                        </label>
                        <textarea
                          id="story"
                          name="story"
                          value={formData.story}
                          onChange={handleInputChange}
                          rows={4}
                          maxLength={2000}
                          className="w-full px-4 py-3 rounded-xl border border-neutral-200 bg-neutral-100 text-neutral-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all resize-y"
                          placeholder="Tell us about your experience preparing with SWTPA and passing your exam..."
                        />
                        <p className="mt-1.5 text-xs text-neutral-500">
                          A few sentences is plenty. No video needed if you write your story here.
                        </p>
                      </div>

                      {/* Video Link */}
                      <div>
                        <label htmlFor="videoLink" className="block text-sm font-medium text-neutral-700 mb-2">
                          Video Link {formData.story.trim() ? '(optional)' : ''}
                        </label>
                        <input
                          type="url"
                          id="videoLink"
                          name="videoLink"
                          value={formData.videoLink}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 rounded-xl border border-neutral-200 bg-neutral-100 text-neutral-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                          placeholder="https://youtube.com/watch?v=... or loom.com/share/..."
                        />
                        <p className="mt-1.5 text-xs text-neutral-500">
                          YouTube (unlisted), Loom, Google Drive, or Dropbox link. Optional if you wrote your story above; at least one is required.
                        </p>
                      </div>

                      {/* Consent Checkbox */}
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id="consent"
                          name="consent"
                          checked={formData.consent}
                          onChange={handleInputChange}
                          required
                          className="mt-1 w-4 h-4 rounded border-neutral-300 text-primary-500 focus:ring-primary-500"
                        />
                        <label htmlFor="consent" className="text-sm text-neutral-600">
                          I agree to allow SWTPA to use my testimonial (story and/or video) for marketing purposes, including on the website, social media, and advertisements. *
                        </label>
                      </div>

                      {/* Submit Button */}
                      <button
                        type="submit"
                        disabled={!isFormValid || isSubmitting}
                        className="w-full py-4 px-6 bg-primary-500 hover:bg-primary-600 disabled:bg-neutral-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-ledge-brand hover:shadow-ledge-lg transition-all duration-200 flex items-center justify-center gap-2"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Upload className="w-5 h-5" />
                            Submit Testimonial
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </div>

              {/* Terms & Fine Print */}
              <div className="mt-6 p-5 bg-neutral-100 rounded-xl border border-neutral-200">
                <div className="flex items-start gap-3 mb-3">
                  <AlertCircle className="w-5 h-5 text-neutral-500 flex-shrink-0 mt-0.5" />
                  <h3 className="font-medium text-neutral-700">
                    Terms & Conditions
                  </h3>
                </div>
                <ul className="space-y-2 text-sm text-neutral-600">
                  <li>- Must be a current or past SWTPA user</li>
                  <li>- We may use your story or video in marketing (website, ads, social media)</li>
                  <li>- One submission per person</li>
                  <li>- SWTPA reserves the right to approve or reject submissions</li>
                  <li>- Free year begins upon approval and code redemption</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Bottom CTA for non-users */}
          <div className="mt-16 text-center">
            <div className="inline-block bg-neutral-100 rounded-2xl shadow-ledge border border-neutral-100 p-6 lg:p-8">
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                Not a SWTPA user yet?
              </h3>
              <p className="text-neutral-600 mb-4">
                Get 7 free days and experience our adaptive learning platform. No card, no billing, nothing to cancel.
              </p>
              <Link
                to="/try"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-xl shadow-ledge-brand hover:shadow-ledge-lg transition-all duration-200"
              >
                Get 7 Free Days
                <ExternalLink className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default TestimonialProgram;
